/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 */

import '../../../types/docs-registry.d.ts';
import { IView, IViewInfo } from "../../../view/IView";
import { IEditor } from "../../../IEditor";
import { LayoutState } from "../../../ui/components/Layout/LayoutState";
import { createDocumentationLayout } from "./Layout";
import { createEventEmitter, emitEvent } from "@adapptio/lib-shared";
import { docsRegistry } from 'docs-registry';

interface IDocumentationParams {
	docPath?: string;
}

export interface IDocumentationViewState {
	selectedPath: string | null;
	history: string[];
	historyIndex: number;
	breadcrumbStack: string[];
}

export interface IDocumentationBreadcrumb {
	path: string;
	title: string;
}

export class DocumentationView implements IView<IDocumentationParams, IDocumentationViewState> {
	public onSelectedChange = createEventEmitter();
	public onViewInfoChanged = createEventEmitter();
	public onHistoryChange = createEventEmitter();
	public onViewStateChanged = createEventEmitter();

	private layout: LayoutState;
	private selectedPath: string | null = null;

	// History tracking (for back/forward navigation)
	private history: string[] = [];
	private historyIndex = -1;
	private isNavigatingHistory = false;

	// Breadcrumb tracking (independent stack)
	private breadcrumbStack: string[] = [];

	constructor(private dc: IEditor) {
		this.layout = createDocumentationLayout();
	}

	public async init(params: IDocumentationParams, _viewId: string, state?: IDocumentationViewState): Promise<void> {
		// Restore from state if available
		if (state) {
			this.history = state.history || [];
			this.historyIndex = state.historyIndex ?? -1;
			this.breadcrumbStack = state.breadcrumbStack || [];

			if (state.selectedPath) {
				this.selectedPath = state.selectedPath;
				emitEvent(this.onSelectedChange);
				emitEvent(this.onViewInfoChanged);
				emitEvent(this.onHistoryChange);
				return;
			}
		}

		// Otherwise use params or default
		if (params?.docPath) {
			this.setSelected(params.docPath);
		} else if (docsRegistry.length > 0) {
			this.setSelected("README");
		}
	}

	public getViewState(): IDocumentationViewState {
		return {
			selectedPath: this.selectedPath,
			history: this.history,
			historyIndex: this.historyIndex,
			breadcrumbStack: this.breadcrumbStack
		};
	}

	public getViewInfo(): IViewInfo {
		// Show page title in tab, or "Documentation" for README/no selection
		let label = "Documentation";

		if (this.selectedPath && this.selectedPath !== "README") {
			const doc = docsRegistry.find(d => d.path === this.selectedPath);
			if (doc?.metadata?.title) {
				label = doc.metadata.title;
			}
		}

		return {
			label,
			icon: "mdi/book",
			canClose: true,
			isModified: false
		};
	}

	public getLayout(): LayoutState {
		return this.layout;
	}

	public getSelected(): string | null {
		return this.selectedPath;
	}

	public setSelected(path: string): void {
		// If not navigating history, add to history
		if (!this.isNavigatingHistory) {
			// Remove any forward history if we're not at the end
			if (this.historyIndex < this.history.length - 1) {
				this.history = this.history.slice(0, this.historyIndex + 1);
			}

			// Add to history if it's a new path
			if (this.history.length === 0 || this.history[this.historyIndex] !== path) {
				this.history.push(path);
				this.historyIndex = this.history.length - 1;
			}
		}

		// Update breadcrumb stack independently
		this.updateBreadcrumbStack(path);

		this.selectedPath = path;
		emitEvent(this.onSelectedChange);
		emitEvent(this.onViewInfoChanged);
		emitEvent(this.onHistoryChange);
		emitEvent(this.onViewStateChanged);
	}

	/**
	 * Update breadcrumb stack - if page exists in stack, pop back to it
	 */
	private updateBreadcrumbStack(path: string): void {
		const existingIndex = this.breadcrumbStack.indexOf(path);

		if (existingIndex !== -1) {
			// Page already in breadcrumbs - pop back to it
			this.breadcrumbStack = this.breadcrumbStack.slice(0, existingIndex + 1);
		} else {
			// New page - add to stack
			this.breadcrumbStack.push(path);
		}
	}

	/**
	 * Navigate back in history
	 */
	public goBack(): void {
		if (!this.canGoBack()) {
			return;
		}

		this.isNavigatingHistory = true;
		this.historyIndex--;
		this.setSelected(this.history[this.historyIndex]);
		this.isNavigatingHistory = false;
	}

	/**
	 * Navigate forward in history
	 */
	public goForward(): void {
		if (!this.canGoForward()) {
			return;
		}

		this.isNavigatingHistory = true;
		this.historyIndex++;
		this.setSelected(this.history[this.historyIndex]);
		this.isNavigatingHistory = false;
	}

	/**
	 * Check if can go back
	 */
	public canGoBack(): boolean {
		return this.historyIndex > 0;
	}

	/**
	 * Check if can go forward
	 */
	public canGoForward(): boolean {
		return this.historyIndex < this.history.length - 1;
	}

	/**
	 * Get breadcrumbs based on breadcrumb stack
	 * Returns array of visited pages with their titles
	 */
	public getBreadcrumbs(): IDocumentationBreadcrumb[] {
		return this.breadcrumbStack.map((path) => {
			const doc = docsRegistry.find(d => d.path === path);

			// Use page title, fallback to "Documentation" for README or path for others
			let title: string;
			if (path === "README") {
				title = "Documentation";
			} else if (doc?.metadata?.title) {
				title = doc.metadata.title;
			} else {
				title = path.split("/").pop() || path;
			}

			return {
				path,
				title
			};
		});
	}

	/**
	 * Opens documentation page in a new tab
	 */
	public openDocumentationInNewTab(docPath: string): void {
		const commandsSvc = this.dc.get("commandsService");
		commandsSvc.execute("documentation.open", docPath, true);
	}

	// Data access layer
	public getDocsRegistry() {
		return docsRegistry;
	}

	public getContent() {
		if (!this.selectedPath) {
			return null;
		}

		const doc = docsRegistry.find(d => d.path === this.selectedPath);
		return doc || null;
	}

	public dispose(): void {
		// Cleanup if needed
	}
}
